import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Button, Stack, styled, TextField } from "~/common/mui";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { getRegionCookie, setRegionCookie } from "~/features/forkPage/utils/regionCookie";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { fetchRegionSiteAndRedirect } from "~/features/regionSelection/components/RegionSelectionBanner";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { MobileCountrySelector } from "~/features/forkPage/components/MobileCountrySelector";
import type { ForkPageCountryDropdownOption, ForkPageCountryOption } from "~/features/forkPage/types";
import { useSession } from "~/app/config/useSession";

export interface formInputs {
  country: string;
}

interface CountrySelectorProps {
  countryOptions: ForkPageCountryOption[];
}

export const CountrySelector = ({ countryOptions: rawCountryOptions }: CountrySelectorProps) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();
  const regionFallbackUrl = Object.values(useSession()?.regionSelection.dropdownUrls ?? {})[0];

  const cookieId = getRegionCookie();
  const countryOptions = useMemo(
    () =>
      rawCountryOptions
        .map(
          (option): ForkPageCountryDropdownOption => ({
            value: option.isocode,
            label: `${t(option.name)} (${option.isocode})`,
            stringLabel: `${t(option.name)} (${option.isocode})`,
            url: option.url,
            isRedirectedCountry: option.isRedirectedCountry,
          }),
        )
        .sort((a, b) => a.stringLabel!.localeCompare(b.stringLabel!)),
    [rawCountryOptions, t],
  );

  const defaultCountry = cookieId?.length > 0 ? cookieId : regionFallbackUrl;

  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountry ?? "");
  const [inputValue, setInputValue] = useState<string>(defaultCountry || "");
  const { val: isOpen, setTrue: openDrawer, setFalse: closeDrawer } = useBooleanState(false);

  const options = countryOptions.map((item) => item.stringLabel ?? "");
  const optionMap = Object.fromEntries(countryOptions.map((item) => [item.stringLabel ?? "", item]));
  const valueMap = Object.fromEntries(countryOptions.map((item) => [item.value ?? "", item]));

  const handleOnChange = useCallback(
    (_event: React.SyntheticEvent, newLabel: unknown, reason: string) => {
      if (reason === "clear" || !newLabel) {
        setSelectedCountry("");
        setInputValue("");
        return;
      }
      const selected = optionMap[newLabel as string];
      if (selected) {
        setSelectedCountry(selected.value);
        setInputValue(selected.stringLabel ?? "");
      }
    },
    [optionMap],
  );

  const handleCountryRedirect = (countryCode: string) => {
    const selected = countryOptions.find((c) => c.value === countryCode);

    if (selected && selected?.url?.length > 0) {
      setRegionCookie(countryCode);

      const url = new URL(selected.url);
      if (selected.isRedirectedCountry) {
        url.searchParams.set("redirectedCountry", countryCode);
      }
      window.location.href = url.toString();
    } else {
      if (regionFallbackUrl) {
        setRegionCookie(regionFallbackUrl);
        fetchRegionSiteAndRedirect(regionFallbackUrl);
      }
    }
  };

  const onSubmit = () => {
    handleCountryRedirect(selectedCountry);
  };

  const handleMobileApply = (label: string) => {
    const matched = countryOptions.find((c) => c.label === label);
    handleCountryRedirect(matched?.value ?? "");
  };

  const handleMobileAutocompleteOpen = () => {
    if (!isTablet) {
      openDrawer();
    }
  };

  return (
    <>
      {isOpen ? (
        <MobileCountrySelector
          selectedCountry={selectedCountry}
          closeDrawer={closeDrawer}
          handleApply={handleMobileApply}
          countryOptions={countryOptions}
        />
      ) : (
        <Container direction={"row"} gap={1}>
          <StyledAutocomplete
            sx={{ width: 305, flex: 1 }}
            value={valueMap[selectedCountry]?.stringLabel ?? ""}
            inputValue={inputValue}
            onInputChange={(_e, value) => setInputValue(value)}
            onChange={handleOnChange}
            options={options}
            disableClearable={selectedCountry.length < 2}
            autoHighlight
            getOptionLabel={(value) => optionMap[value as string]?.stringLabel || ""}
            onOpen={handleMobileAutocompleteOpen}
            noOptionsText={t("forkPage.country.search.noresults")}
            loadingText={t("spinner.text")}
            renderOption={(props, label) => (
              <li {...props} key={`key-${label}`}>
                <TextWithHighlight highlight={inputValue} highlightClass={"ellipsis"}>
                  {label as string}
                </TextWithHighlight>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={t("forkPage.country.input")} />}
          />
          <ApplyButton size={"medium"} className="ellipsis" onClick={onSubmit} disabled={selectedCountry.length < 2}>
            {t("forkPage.apply.btn")}
          </ApplyButton>
        </Container>
      )}
    </>
  );
};

const Container = styled(Stack)({
  flexWrap: "wrap",
  alignItems: "center",
});

const ApplyButton = styled(Button)({
  flexShrink: 0,
});

export const StyledAutocomplete = styled(Autocomplete)({
  "& .MuiAutocomplete-input": {
    marginRight: "32px !important",
  },
});
